import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import ComparisonModal from '../components/ComparisonModal';
import { useToast } from '../../../components/ui/Toast';
import { regularizationService, getErrorMessage } from '../../../services/apiService';

const BatchDetails = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchBatchDetails();
  }, [batchId, isAdmin, navigate]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await regularizationService.getBatchDetails(Number(batchId));
      if (response.success) {
        setBatch(response.data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to fetch batch details');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (requestId) => {
    try {
      const response = await regularizationService.verifyRequest(requestId);
      if (response.success) {
        toast.success('Vendor changes verified and applied successfully');
        fetchBatchDetails();
        setShowComparison(false);
        setSelectedRequest(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to verify request');
    }
  };

  const handleReviewChanges = (request) => {
    setSelectedRequest(request);
    setShowComparison(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      SENT: { variant: 'info', label: 'Sent' },
      SUBMITTED: { variant: 'warning', label: 'Submitted' },
      VERIFIED: { variant: 'success', label: 'Verified' },
      EXPIRED: { variant: 'destructive', label: 'Expired' },
    };
    const config = statusConfig[status] || statusConfig.SENT;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-600">Batch not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusCounts = batch.requests.reduce((acc, req) => {
    acc[req.status] = (acc[req.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6">
      <Breadcrumb
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Vendor Regularization', path: '/vendor-regularization' },
          { label: `Batch #${batchId}`, path: `/vendor-regularization/batch/${batchId}` },
        ]}
      />

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Batch #{batchId}</h1>
            <p className="text-gray-600 mt-1">
              Sent on {new Date(batch.sent_at).toLocaleDateString()}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/vendor-regularization')}>
            <Icon name="ArrowLeft" size={18} className="mr-2" />
            Back to List
          </Button>
        </div>
      </div>

      {/* Batch Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 mb-1">Total Vendors</p>
            <p className="text-2xl font-bold text-gray-900">{batch.requests.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 mb-1">Sent</p>
            <p className="text-2xl font-bold text-gray-900">{statusCounts.SENT || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 mb-1">Submitted</p>
            <p className="text-2xl font-bold text-amber-600">{statusCounts.SUBMITTED || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 mb-1">Verified</p>
            <p className="text-2xl font-bold text-emerald-600">{statusCounts.VERIFIED || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Vendor List */}
      <div className="space-y-4">
        {batch.requests.map((request) => (
          <Card key={request.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {request.vendor.company_name}
                    </h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span>Vendor Code: {request.vendor.vendor_code}</span>
                  </div>
                  {request.submitted_at && (
                    <p className="text-xs text-gray-500 mt-2">
                      Submitted on {new Date(request.submitted_at).toLocaleDateString()}
                    </p>
                  )}
                  {request.verified_at && (
                    <p className="text-xs text-green-600 mt-2">
                      Verified on {new Date(request.verified_at).toLocaleDateString()}
                      {request.verifier && ` by ${request.verifier.full_name}`}
                    </p>
                  )}
                </div>
                <div>
                  {request.status === 'SUBMITTED' && (
                    <Button onClick={() => handleReviewChanges(request)}>
                      Review Changes
                    </Button>
                  )}
                  {request.status === 'VERIFIED' && (
                    <span className="text-sm text-emerald-600 font-medium">Verified</span>
                  )}
                  {request.status === 'EXPIRED' && (
                    <span className="text-sm text-red-600 font-medium">Expired</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showComparison && selectedRequest && (
        <ComparisonModal
          request={selectedRequest}
          onClose={() => {
            setShowComparison(false);
            setSelectedRequest(null);
          }}
          onVerify={() => handleVerify(selectedRequest.id)}
        />
      )}
    </div>
  );
};

export default BatchDetails;
